import { ApiResult, HttpClient } from "../http.js";
import { CalculationTemplateDto } from "../types/calculation-templates.js";

/**
 * Sub-item template input. Ids are server-assigned on create/update, so the
 * write payload omits them.
 */
export interface CalculationSubItemTemplateInput {
  name: string;
  articleNumber?: string | null;
  isCalculated?: boolean | null;
  formula?: string | null;
  sortOrder: number;
}

export interface CalculationCategoryInput {
  name: string;
  categoryCode?: string | null;
  subItems?: CalculationSubItemTemplateInput[] | null;
  sortOrder: number;
}

export interface CreateCalculationTemplatePayload {
  name: string;
  description?: string | null;
  hourlyRate: number;
  socialCostMultiplier: number;
  profitMarginPercent?: number | null;
  categories: CalculationCategoryInput[];
  isDefault: boolean;
  id?: string;
}

export interface UpdateCalculationTemplatePayload {
  name: string;
  description?: string | null;
  hourlyRate: number;
  socialCostMultiplier: number;
  profitMarginPercent?: number | null;
  categories: CalculationCategoryInput[];
}

/**
 * Company-level commercial-calculation templates.
 * Server route: `/api/settings/calculation-templates`.
 */
export class CalculationTemplatesApi {
  constructor(private readonly http: HttpClient) {}

  list(): Promise<ApiResult<CalculationTemplateDto[]>> {
    return this.http.get<CalculationTemplateDto[]>("/api/settings/calculation-templates");
  }

  get(id: string): Promise<ApiResult<CalculationTemplateDto>> {
    return this.http.get<CalculationTemplateDto>(
      `/api/settings/calculation-templates/${encodeURIComponent(id)}`,
    );
  }

  create(payload: CreateCalculationTemplatePayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/settings/calculation-templates", { body: payload });
  }

  update(id: string, payload: UpdateCalculationTemplatePayload): Promise<ApiResult<string>> {
    return this.http.put<string>(
      `/api/settings/calculation-templates/${encodeURIComponent(id)}`,
      { body: payload },
    );
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(
      `/api/settings/calculation-templates/${encodeURIComponent(id)}`,
    );
  }

  setDefault(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(
      `/api/settings/calculation-templates/${encodeURIComponent(id)}/set-default`,
    );
  }
}
